import { put, call, fork } from 'redux-saga/effects';
import * as actionsNO05 from '../actions/indexNO05';
import * as APICalls from '../../felles/APICalls_NO05';
import NO05, { NO05StatusStat } from '../../models/NO05';
import { KONTANT_PAYMENT_METHODS, DIREKTE_ORDER_TYPES, isPlukkOrdre, hasBkCode } from '../../pages/NO05/no05StatusGroups';

function getErrorMessage(error: any, fallback: string): string {
    const backendMessage = error?.response?.data?.error;
    return typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage : fallback;
}

function mapToNO05(item: any): NO05 {
    const lines: any[] = item.products ?? [];
    const antBestilt = lines.reduce((s: number, l: any) => s + (l.quantityOrdered ?? 0), 0);
    const antLevert = lines.reduce((s: number, l: any) => s + (l.quantityDelivered ?? 0), 0);
    return new NO05(
        item.orderNumber ?? '',
        item.customerNumber ?? '',
        item.customerName ?? '',
        item.requisitionNumber ?? '',
        item.orderDate ?? '',
        item.orderTime ?? '',
        item.receiver ?? '',
        item.status ?? 'Ufullstendig',
        antBestilt, antLevert,
        item.totalPrice ?? 0,
        item.grossProfit ?? 0,
        item.invoiceCreditNumber ?? '',
        item.shippingNumber ?? '',
        String(item.orderType ?? ''),
        item.orderPaymentMethod ?? '',
        item.shippingMethod ?? '',
        item.speditor ?? '',
        String(item.directOrderFlag ?? ''),
        String(item.skaff ?? ''),
        String(item.bk ?? ''),
    );
}

const PREFERRED_STATUS_ORDER = [
    'Ferdigmeldt', 'Fmeld./KS-oppgjør', 'Ferdig plukket', 'Printet', 'Pakket',
    'Ferdig registrert', 'Venter godkjenning', 'Påbegynt, ikke fullført',
    'Kred-stopp, ikke print', 'Lagerovf. (f.meldt)', 'Verkstedsord. (f.meldt)',
    'Utgårmeldt', 'Korr. etter f.melding', 'Restreg. (f.meldt)', 'Retur til lev.',
    'Delferdigmeldt', 'Påbegynt samleordre', 'Sluppet forhåndsordre', 'Ufullstendig',
];

function computeStats(orders: NO05[]): NO05StatusStat[] {
    type Acc = { count: number; totalAmount: number; brfSum: number; antBestilt: number; antLevert: number };

    const returs = orders.filter((o) => o.ordreNr.startsWith('9'));

    const groups: Record<string, Acc> = {};
    for (const o of orders) {
        const key = o.status;
        if (!groups[key]) groups[key] = { count: 0, totalAmount: 0, brfSum: 0, antBestilt: 0, antLevert: 0 };
        groups[key].count++;
        groups[key].totalAmount += o.belop;
        groups[key].brfSum      += o.brfProsent;
        groups[key].antBestilt  += o.antBestilt;
        groups[key].antLevert   += o.antLevert;
    }

    const toStat = (status: string, g: Acc): NO05StatusStat => ({
        status,
        count:       g.count,
        totalAmount: g.totalAmount,
        brfProsent:  g.count > 0 ? g.brfSum / g.count : 0,
        antBestilt:  g.antBestilt,
        antLevert:   g.antLevert,
    });

    const allStat: NO05StatusStat = {
        status:      'Alle ordre',
        count:       orders.length,
        totalAmount: orders.reduce((s, o) => s + o.belop, 0),
        brfProsent:  orders.length > 0 ? orders.reduce((s, o) => s + o.brfProsent, 0) / orders.length : 0,
        antBestilt:  orders.reduce((s, o) => s + o.antBestilt, 0),
        antLevert:   orders.reduce((s, o) => s + o.antLevert, 0),
    };

    const stats: NO05StatusStat[] = [allStat];

    for (const name of PREFERRED_STATUS_ORDER) {
        if (groups[name]) {
            stats.push(toStat(name, groups[name]));
            delete groups[name];
        }
    }
    for (const [name, g] of Object.entries(groups)) {
        stats.push(toStat(name, g));
    }

    if (returs.length > 0) {
        stats.push({
            status:      'Retur',
            count:       returs.length,
            totalAmount: returs.reduce((s, o) => s + o.belop, 0),
            brfProsent:  returs.length > 0 ? returs.reduce((s, o) => s + o.brfProsent, 0) / returs.length : 0,
            antBestilt:  returs.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   returs.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const kontant = orders.filter((o) => KONTANT_PAYMENT_METHODS.includes(o.betalingsKode));
    if (kontant.length > 0) {
        stats.push({
            status:      'Kontant',
            count:       kontant.length,
            totalAmount: kontant.reduce((s, o) => s + o.belop, 0),
            brfProsent:  kontant.reduce((s, o) => s + o.brfProsent, 0) / kontant.length,
            antBestilt:  kontant.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   kontant.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const direkte = orders.filter((o) => DIREKTE_ORDER_TYPES.includes(o.direkteFlagg));
    if (direkte.length > 0) {
        stats.push({
            status:      'Direkte ordre',
            count:       direkte.length,
            totalAmount: direkte.reduce((s, o) => s + o.belop, 0),
            brfProsent:  direkte.reduce((s, o) => s + o.brfProsent, 0) / direkte.length,
            antBestilt:  direkte.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   direkte.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const plukk = orders.filter((o) => isPlukkOrdre(o.skaff));
    if (plukk.length > 0) {
        stats.push({
            status:      'Plukkordre',
            count:       plukk.length,
            totalAmount: plukk.reduce((s, o) => s + o.belop, 0),
            brfProsent:  plukk.reduce((s, o) => s + o.brfProsent, 0) / plukk.length,
            antBestilt:  plukk.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   plukk.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const bkCode = orders.filter((o) => hasBkCode(o.bk));
    if (bkCode.length > 0) {
        stats.push({
            status: 'Ordre med BK kode',
            count:       bkCode.length,
            totalAmount: bkCode.reduce((s, o) => s + o.belop, 0),
            brfProsent:  bkCode.reduce((s, o) => s + o.brfProsent, 0) / bkCode.length,
            antBestilt:  bkCode.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   bkCode.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    return stats;
}

// Initial page load, split into two independent, concurrent fetches instead of one combined
// call — the table no longer has to wait on the slow full/unpaged fetch that the status cards
// need for accurate counts:
//  - quickInitialTableLoad: one fast, small page → table renders almost immediately.
//  - fullInitialStatsLoad: the full open-order set (unchanged from before) → status cards
//    (hidden until this arrives, same as today), and once it lands it also replaces the
//    table's quick page with the complete set so status-card filtering has everything to
//    filter against. Forked so a rapid second search (takeLatest) cancels both cleanly.
function* quickInitialTableLoad(companyId: string) {
  try {
    const response: any = yield call(APICalls.searchOpenOrders, { companyId, page: 0, pageSize: 30 });
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      // hasMore here just controls scroll-triggered paging until the full fetch below lands
      // and replaces this quick page outright (see fullInitialStatsLoad).
      yield put(actionsNO05.NO05SearchSuccess(raw.map(mapToNO05), raw.length >= 30));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke hente åpne ordre. Prøv igjen.')));
  }
}

function* fullInitialStatsLoad(companyId: string) {
  try {
    yield put(actionsNO05.NO05StatsRequest(companyId));
    const response: any = yield call(APICalls.loadOpenOrdersStats, companyId);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const orders: NO05[] = raw.map(mapToNO05);
      yield put(actionsNO05.NO05StatsSuccess(computeStats(orders)));
      yield put(actionsNO05.NO05SearchSuccess(orders, false));
    }
  } catch (error: any) {
    // Only the stats/cards side fails here — the table already has the quick page from
    // quickInitialTableLoad (a genuinely separate, unrelated request), and NO05SearchFailure
    // would wipe that out (the reducer clears `orders` on that action). Leave it standing.
    yield put(actionsNO05.NO05StatsFailure(getErrorMessage(error, 'Kunne ikke hente statuskort. Prøv igjen.')));
  }
}

export function* searchNO05OrdersSaga(action: any) {
  try {
    const isInitialLoad = action.payload.skipPaging === true;

    if (isInitialLoad) {
      yield fork(quickInitialTableLoad, action.payload.companyId);
      yield fork(fullInitialStatsLoad, action.payload.companyId);
    } else {
      // Filtered search — stats reflect the search results
      const response: any = yield call(APICalls.searchOpenOrders, { ...action.payload, page: 0, pageSize: 100 });
      if (response?.data) {
        const raw: any[] = response.data.orders ?? response.data.content ?? [];
        const orders: NO05[] = raw.map(mapToNO05);
        yield put(actionsNO05.NO05StatsSuccess(computeStats(orders)));
        yield put(actionsNO05.NO05SearchSuccess(orders, raw.length >= 100));
      }
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05SearchFailure(getErrorMessage(error, 'Kunne ikke søke etter ordre. Prøv igjen.')));
  }
}

export function* loadMoreNO05OrdersSaga(action: any) {
  try {
    // Strip skipPaging — load-more always uses regular paging
    const { skipPaging: _, ...pagedParams } = action.payload;
    const response = yield call(APICalls.searchOpenOrders, pagedParams);
    if (response?.data) {
      const raw: any[] = response.data.orders ?? response.data.content ?? [];
      const newOrders: NO05[] = raw.map(mapToNO05);
      const hasMore = raw.length >= (pagedParams.pageSize ?? 30);

      yield put(actionsNO05.NO05LoadMoreSuccess(newOrders, hasMore));
    }
  } catch (error: any) {
    yield put(actionsNO05.NO05LoadMoreFailure(getErrorMessage(error, 'Kunne ikke laste flere ordre. Prøv igjen.')));
  }
}

export function* fetchNO05OrderDetailSaga(action: any) {
  try {
    const { companyId, orderNumber } = action.payload;
    const response = yield call(APICalls.fetchOrderDetail, companyId, orderNumber);
    yield put(actionsNO05.NO05OrderDetailSuccess(response.data));
  } catch (error: any) {
    yield put(actionsNO05.NO05OrderDetailFailure(getErrorMessage(error, 'Kunne ikke hente ordredetaljer. Prøv igjen.')));
  }
}
